import { FC, useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import { ISelectOptionProps } from 'components/form/types';
import FormGroup from 'components/form/FormGroup';
import Form from 'components/form/Form';
import FormRowGroup from 'components/form/FormRowGroup';
import { IInputs } from './types';

interface IProps {
  methods: any;
  handleSubmit: (data: IInputs) => void;
  btnLabel: string;
  isEditMode?: boolean;
  typeOptions: ISelectOptionProps[];
  langOptions: ISelectOptionProps[];
}

const ClientForm: FC<IProps> = ({ methods, handleSubmit, btnLabel, isEditMode, typeOptions, langOptions }: IProps) => {
  const { watch, unregister, setValue } = methods;

  // watch to enable re-render when company type is changed
  const companyType = watch('type') as ISelectOptionProps;
  const templatesValue = watch('templates') as boolean;
  const isMultilingual = watch('is_multilingual') as boolean;
  const isLeavesActive = watch('leaves') as boolean;
  const showColleagueLeaves = watch('show_colleagues_leaves') as boolean;
  const isReminderActive = watch('employee_reminder_email') as boolean;
  const isDisallowLeavesEOM = watch('disallow_leaves_near_end_of_month') as boolean;
  const isSubstituteEmployee = watch('leave_substitute_employee') as boolean;
  const isLeavesHalfDay = watch('leaves_half_day') as boolean;
  const isForm76Active = watch('form_76') as boolean;
  const isPaidAdditionalLeaveActive = watch('paid_additional_leave') as boolean;

  // Set default values for new client only
  useEffect(() => {
    if (!isEditMode) {
      setValue('is_active', true);
      setValue('filtered_by_role_permission', true);
      setValue('limit_documents_to_leaves', true);
      setValue('templates', true);
      setValue('limit_notifications', true);
      setValue('show_wfh_leave_type_in_main_dropdown', true);
    }
  }, [isEditMode, setValue]);

  // Handle dynamic defaults when Leaves Module is toggled (only for new clients)
  useEffect(() => {
    if (!isEditMode && isLeavesActive) {
      setValue('hide_leave_templates_from_form', true);
      setValue('disallow_approving_own_leaves', true);
      setValue('leave_review', true);
    }
  }, [isLeavesActive, isEditMode, setValue]);

  useEffect(() => {
    if (companyType?.value !== 'non_government') unregister('fondation');
    if (companyType?.value !== 'municipality') {
      unregister([
        'second_representative_names',
        'second_representative_egn',
        'second_representative_position',
        'third_representative_names',
        'third_representative_egn',
        'third_representative_position',
      ]);
    }
  }, [companyType, unregister]);

  const getCompanyNameLabel = (lang: 'bg' | 'en') => {
    const labels: {
      bg: {
        [key: string]: string;
      };
      en: {
        [key: string]: string;
      };
    } = {
      bg: {
        main_company: 'Име на компанията',
        free_profession: 'Три имена',
        others: 'Наименование',
      },
      en: {
        main_company: 'Company name',
        free_profession: 'Three names of person',
        others: 'Name',
      },
    };

    return (companyType && labels[lang][companyType.value.toString()]) || labels[lang].others;
  };

  const getHqLabel = (lang: 'bg' | 'en') => {
    const labels: {
      bg: {
        [key: string]: string;
      };
      en: {
        [key: string]: string;
      };
    } = {
      bg: {
        free_profession: 'Постоянен адрес',
        others: 'Седалище',
      },
      en: {
        free_profession: 'Main address',
        others: 'Address',
      },
    };

    return (companyType && labels[lang][companyType.value.toString()]) || labels[lang].others;
  };

  return (
    <Form
      methods={methods}
      onSubmit={handleSubmit}
      btnLabel={btnLabel}
      isHorizontal
    >
      <FormGroup
        controlId="formGroupSubdomain"
        label="Поддомейн"
        type="text"
        name="domain"
        isRequired
        isHorizontal
      />
      <FormGroup
        controlId="formGroupCompanyType"
        label="Тип на компанията"
        type="select"
        as="select"
        name="type"
        isDisabled={isEditMode}
        isRequired
        isHorizontal
        selectOptions={typeOptions}
      />
      <FormGroup
        controlId="formGroupDefaultLang"
        label="Основен език"
        type="select"
        as="select"
        name="default_lang"
        isRequired
        isHorizontal
        selectOptions={langOptions}
      />
      {/** Hide all company details if no company type is selected */}
      {companyType && (
        <>
          {isMultilingual ? (
            <FormRowGroup>
              <FormGroup
                controlId="formGroupCompanyName"
                label={getCompanyNameLabel('bg')}
                type="text"
                name="company_name"
                isRequired
                isHorizontal
                isRowGroup
              />
              <FormGroup
                controlId="formGroupCompanyNameEn"
                label={getCompanyNameLabel('en')}
                type="text"
                name="company_name_en"
                isRequired
                isHorizontal
                isRowGroup
              />
            </FormRowGroup>
          ) : (
            <FormGroup
              controlId="formGroupCompanyName"
              label={getCompanyNameLabel('bg')}
              type="text"
              name="company_name"
              isRequired
              isHorizontal
            />
          )}
          {companyType.value === 'non_government' && (
            <>
              {isMultilingual ? (
                <FormRowGroup>
                  <FormGroup
                    controlId="formGroupFoundation"
                    label="Фондация"
                    type="text"
                    name="fondation"
                    isRequired
                    isHorizontal
                    isRowGroup
                  />
                  <FormGroup
                    controlId="formGroupFoundationEn"
                    label="Fondation"
                    type="text"
                    name="fondation_en"
                    isRequired
                    isHorizontal
                    isRowGroup
                  />
                </FormRowGroup>
              ) : (
                <FormGroup
                  controlId="formGroupFoundation"
                  label="Фондация"
                  type="text"
                  name="fondation"
                  isRequired
                  isHorizontal
                />
              )}
            </>
          )}
          {isMultilingual ? (
            <FormRowGroup>
              <FormGroup
                controlId="formGroupHq"
                label={getHqLabel('bg')}
                type="text"
                name="hq"
                isRequired
                isHorizontal
                isRowGroup
              />
              <FormGroup
                controlId="formGroupHqEn"
                label={getHqLabel('en')}
                type="text"
                name="hq_en"
                isRequired
                isHorizontal
                isRowGroup
              />
            </FormRowGroup>
          ) : (
            <FormGroup
              controlId="formGroupHq"
              label={getHqLabel('bg')}
              type="text"
              name="hq"
              isRequired
              isHorizontal
            />
          )}
          {isMultilingual ? (
            <FormRowGroup>
              <FormGroup
                controlId="formGroupManagementAddress"
                label="Адрес на управление"
                type="text"
                name="management_address"
                isRequired
                isHorizontal
                isRowGroup
              />
              <FormGroup
                controlId="formGroupManagementAddressEn"
                label="Мanagement address"
                type="text"
                name="management_address_en"
                isRequired
                isHorizontal
                isRowGroup
              />
            </FormRowGroup>
          ) : (
            <FormGroup
              controlId="formGroupManagementAddress"
              label="Адрес на управление"
              type="text"
              name="management_address"
              isRequired
              isHorizontal
            />
          )}
          <FormGroup
            controlId="formGroupEik"
            label="ЕИК/БУЛСТАТ"
            type="text"
            name="eik"
            isRequired
            isHorizontal
          />
          <FormGroup
            controlId="formGroupDds"
            label="ДДС номер"
            type="text"
            name="dds_number"
            isHorizontal
          />
          {isMultilingual ? (
            <FormRowGroup>
              <FormGroup
                controlId="formGroupMailingAddress"
                label="Адрес за кореспонденция"
                type="text"
                name="mailing_address"
                isRequired
                isHorizontal
                isRowGroup
              />
              <FormGroup
                controlId="formGroupMailingAddressEn"
                label="Mailing address"
                type="text"
                name="mailing_address_en"
                isRequired
                isHorizontal
                isRowGroup
              />
            </FormRowGroup>
          ) : (
            <FormGroup
              controlId="formGroupMailingAddress"
              label="Адрес за кореспонденция"
              type="text"
              name="mailing_address"
              isRequired
              isHorizontal
            />
          )}
          <FormGroup
            controlId="formGroupEmail"
            label="E-mail"
            type="email"
            name="email"
            isRequired
            isHorizontal
          />
          <FormGroup
            controlId="formGroupPhone"
            label="Телефон"
            type="text"
            name="phone_number"
            isRequired
            isHorizontal
          />
          <FormGroup
            controlId="formGroupIban"
            label="Активен IBAN"
            type="text"
            name="iban"
            isRequired
            isHorizontal
          />
          <FormGroup
            controlId="formGroupAbsenceWebhookUrl"
            label="Webhook URL за отпуски"
            type="text"
            name="absence_webhook_url"
            isHorizontal
          />
          {companyType.value === 'municipality' && (
            <>
              {isMultilingual ? (
                <FormRowGroup>
                  <FormGroup
                    controlId="formGroupSecondRepresentativeNames"
                    label="Три имена на втори представляващ"
                    type="text"
                    name="second_representative_names"
                    isHorizontal
                    isRowGroup
                  />
                  <FormGroup
                    controlId="formGroupSecondRepresentativeNamesEn"
                    label="Three names of second representative"
                    type="text"
                    name="second_representative_names_en"
                    isHorizontal
                    isRowGroup
                  />
                </FormRowGroup>
              ) : (
                <FormGroup
                  controlId="formGroupSecondRepresentativeNames"
                  label="Три имена на втори представляващ"
                  type="text"
                  name="second_representative_names"
                  isHorizontal
                />
              )}
              <FormGroup
                controlId="formGroupSecondRepresentativeEGN"
                label="ЕГН на втори представляващ"
                type="text"
                name="second_representative_egn"
                isEGN
                isHorizontal
              />
              {isMultilingual ? (
                <FormRowGroup>
                  <FormGroup
                    controlId="formGroupSecondRepresentativePosition"
                    label="Длъжност на втори представляващ"
                    type="text"
                    name="second_representative_position"
                    isHorizontal
                    isRowGroup
                  />
                  <FormGroup
                    controlId="formGroupSecondRepresentativePositionEn"
                    label="Second representative position"
                    type="text"
                    name="second_representative_position_en"
                    isHorizontal
                    isRowGroup
                  />
                </FormRowGroup>
              ) : (
                <FormGroup
                  controlId="formGroupSecondRepresentativePosition"
                  label="Длъжност на втори представляващ"
                  type="text"
                  name="second_representative_position"
                  isHorizontal
                />
              )}
              {isMultilingual ? (
                <FormRowGroup>
                  <FormGroup
                    controlId="formGroupThirdRepresentativeNames"
                    label="Три имена на трети представляващ"
                    type="text"
                    name="third_representative_names"
                    isHorizontal
                    isRowGroup
                  />
                  <FormGroup
                    controlId="formGroupThirdRepresentativeNamesEn"
                    label="Three names of third representative"
                    type="text"
                    name="third_representative_names_en"
                    isHorizontal
                    isRowGroup
                  />
                </FormRowGroup>
              ) : (
                <FormGroup
                  controlId="formGroupThirdRepresentativeNames"
                  label="Три имена на трети представляващ"
                  type="text"
                  name="third_representative_names"
                  isHorizontal
                />
              )}
              <FormGroup
                controlId="formGroupThirdRepresentativeEGN"
                label="ЕГН на трети представляващ"
                type="text"
                name="third_representative_egn"
                isEGN
                isHorizontal
              />
              {isMultilingual ? (
                <FormRowGroup>
                  <FormGroup
                    controlId="formGroupThirdRepresentativePosition"
                    label="Длъжност на трети представляващ"
                    type="text"
                    name="third_representative_position"
                    isHorizontal
                    isRowGroup
                  />
                  <FormGroup
                    controlId="formGroupThirdRepresentativePositionEn"
                    label="Third representative position"
                    type="text"
                    name="third_representative_position_en"
                    isHorizontal
                    isRowGroup
                  />
                </FormRowGroup>
              ) : (
                <FormGroup
                  controlId="formGroupThirdRepresentativePosition"
                  label="Длъжност на трети представляващ"
                  type="text"
                  name="third_representative_position"
                  isHorizontal
                />
              )}
            </>
          )}
        </>
      )}
      <hr />
      <Row className="offset-md-2">
        <Col md={5}>
          <FormGroup
            controlId="formGroupActive"
            label="Активен"
            checkType="switch"
            name="is_active"
            isRequired
          />
          <FormGroup
            controlId="formGroupIsDemoClient"
            label="Демо клиент"
            checkType="switch"
            name="is_demo"
            isRequired
          />
          <FormGroup
            controlId="formGroupRequiredSignature"
            label="Задължителен ел. подпис"
            checkType="switch"
            name="required_electronic_sign"
            isRequired
          />
          <p className="custom-switch__desc text-muted small mb-2">Не се използва в момента</p>
        </Col>
      </Row>
      <hr />
      <Row className="offset-md-2">
        <Col md={5}>
          <FormGroup
            controlId="formGroupTemplates"
            label="Възможност за специфични шаблони"
            checkType="switch"
            name="templates"
            isRequired
          />
          <p className="custom-switch__desc text-muted small mb-2">
            Може да създава и редактира специфични шаблони за документи
          </p>
          {templatesValue && (
            <>
              <FormGroup
                controlId="formGroupIsMultilingual"
                label="Възможност за мултиезични шаблони"
                checkType="switch"
                name="is_multilingual"
                isRequired
              />
              <p className="custom-switch__desc text-muted small mb-2">
                Може да създава, редактира и управлява шаблони на различни езици
              </p>
            </>
          )}
          <FormGroup
            controlId="formGroupIsFilterByPermission"
            label="Ограничения по права"
            checkType="switch"
            name="filtered_by_role_permission"
            isRequired
          />
          <p className="custom-switch__desc text-muted small mb-2">
            Ограничава се достъпа до категории документи, спрямо ролята на потребителя.
          </p>
          <FormGroup
            controlId="formPermissionsScope"
            label="Възможност за ограничение по групи спрямо категория"
            checkType="switch"
            name="permissions_scope"
            isRequired
          />
          <p className="custom-switch__desc text-muted small mb-2">
            Ограничаване достъпа на модератори, спрямо категория документи и групи. Може да се ограничи достъп и до
            чувствителни данни (ЕГН, ЛК номер, заплата)
          </p>
          <FormGroup
            controlId="formGroupRoleLimits"
            label="Ограничения по групи и роли"
            checkType="switch"
            name="group_role_limits"
            isRequired
          />
          <p className="custom-switch__desc text-muted small mb-2">
            Ограничаване възможни действия на модератор, спрямо ролите му и достъпните му групи служители. Основно за
            отпуски.
          </p>
          <FormGroup
            controlId="formGroupLimitNotifications"
            label="Ограничаване на известията след връщане на документ"
            checkType="switch"
            name="limit_notifications"
          />
          <p className="custom-switch__desc text-muted small mb-2">
            След връщане на документ от служител известията се изпращат само до потребителите, които реално са
            изпълнявали действия по документа, а не всички с ролята. Работи, само когато са зададени конкретни роли. Не
            изключва сложените изрично роли, настроени да получават известия
          </p>
          <FormGroup
            controlId="formGroupPerUserNotificationLimits"
            label="Прилагай ограничения за уведомления по група/роля/категория."
            checkType="switch"
            name="per_user_notification_limits"
          />
          <p className="custom-switch__desc text-muted small mb-2">
            Позволява ограничаване уведомленията. Настройките са през базата.
          </p>
          <FormGroup
            controlId="formGroupHealthAndSafetyTraining"
            label="Здравословни и безопасни условия на труд"
            checkType="switch"
            name="health_and_safety_training"
          />
          <p className="custom-switch__desc text-muted small mb-2">
            Визуализира модул ЗБУТ за управление на инструктажи на служители
          </p>
          <FormGroup
            controlId="formGroupWorkTrips"
            label="Командировки"
            checkType="switch"
            name="work_trips"
          />
          <p className="custom-switch__desc text-muted small mb-2">
            Визуализира модул за създаване и управление на командировки
          </p>
        </Col>
        <Col md={5}>
          <FormGroup
            controlId="formGroupReminderEmail"
            label="Напомняния до служители за незавършени документи"
            checkType="switch"
            name="employee_reminder_email"
          />
          <p className="custom-switch__desc text-muted small mb-2">
            Може да се изпращат уведомления за незавършени действия по документи в системата
          </p>
          {isReminderActive && (
            <Row>
              <Col md={6}>
                <FormGroup
                  controlId="formGroupReminderEmailDelay"
                  label="Изпращане на уведомление след N часа"
                  type="number"
                  min={1}
                  name="employee_reminder_email_check_period"
                  isWholeNumber
                  isRequired
                />
              </Col>
              <Col md={6}>
                <FormGroup
                  controlId="formGroupReminderEmailTimes"
                  label="Брой уведомления за документ"
                  type="number"
                  min={1}
                  name="employee_reminder_email_max_reminder_count_per_document"
                  isWholeNumber
                  isRequired
                />
              </Col>
            </Row>
          )}
          <FormGroup
            controlId="formGroupReminderForProbationPeriodEnd"
            label="Напомняне за изтичане на изпитателен срок"
            checkType="switch"
            name="reminder_for_probation_period_end"
          />
          <p className="custom-switch__desc text-muted small mb-2">
            Автоматично напомняне за предстоящо изтичане на изпитателен срок на служител в началото на месеца и в
            седмицата конкретно когато се случва. Следи и дните отпуск. Изпраща се до забит списък с имена на роли.
          </p>
          <FormGroup
            controlId="formGroupReminderForIdCardExpiration"
            label="Напомняне за изтичане на лична карта"
            checkType="switch"
            name="reminder_for_id_card_expiration"
          />
          <p className="custom-switch__desc text-muted small mb-2">
            Автоматично напомняне за предстоящото изтичане на валидността личната карта на служители. Изпраща се до
            забит списък с имена на роли.
          </p>
          <FormGroup
            controlId="formGroupIsForm76"
            label="Форма 76"
            checkType="switch"
            name="form_76"
            isRequired
          />
          <p className="custom-switch__desc text-muted small mb-2">Позволява генериране на форма 76</p>
          {isForm76Active && (
            <>
              <FormGroup
                controlId="formGroupNightWork"
                label="Колона нощни във форма 76"
                checkType="switch"
                name="night_work"
                isRequired
              />
              <p className="custom-switch__desc text-muted small mb-2">
                Позволява визуализиране на колона за нощни смени във форма 76
              </p>
            </>
          )}
          <FormGroup
            controlId="formGroupExitInterview"
            label="Подкана за попълване на изходящо интервю"
            checkType="switch"
            name="exit_interview"
          />
          <p className="custom-switch__desc text-muted small mb-2">
            При документи с определени идентификатори от категория Прекратяване показва подкана за попълване на изходящо
            интервю при напускане на служител. Линка трябва да се въведе ръчно в базата.
          </p>
          <FormGroup
            controlId="formGroupEmployeeCandidates"
            label="Добавяне на кандидати"
            checkType="switch"
            name="employee_candidates"
          />
          <p className="custom-switch__desc text-muted small mb-2">
            Позволява създаване на нови кандидати в системата (в момента е със забит текст за конкретен клиент).
            Ограничава и дали има подписано споразумение и т.н., спрямо идентификатори.
          </p>
          <FormGroup
            controlId="formGroupIsCustomForClientF"
            label="Специфични полета клиент F"
            checkType="switch"
            name="custom_feature_for_client_f"
            isRequired
          />
          <p className="custom-switch__desc text-muted small mb-2">
            Добавено за конкретен клиент. Добавя 2 custom полета на служител и прави мобилен задължителен.
          </p>
        </Col>
      </Row>

      <hr />

      <Row className="offset-md-2">
        <Col md={5}>
          <FormGroup
            controlId="formGroupLeaves"
            label="Модул за отпуски"
            checkType="switch"
            name="leaves"
            isRequired
          />
          <p className="custom-switch__desc text-muted small mb-2">
            Визуализира модул за създаване, заявяване и управление на отпуски
          </p>
        </Col>
      </Row>
      {isLeavesActive && (
        <Row className="offset-md-2">
          <Col md={5}>
            <FormGroup
              controlId="formGroupLimitDocumentsToLeaves"
              label="Лимитирай служители до отпуски"
              checkType="switch"
              name="limit_documents_to_leaves"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Лимитиране само до документи от шаблони, и само категория отпуски за служители.
            </p>
            <FormGroup
              controlId="formGroupHideLeaveTemplates"
              label="Скриване шаблони с идентификатор за отпуск за служител"
              checkType="switch"
              name="hide_leave_templates_from_form"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Контролира показването на шаблони, свързани с отпуските на служителите, за които има зададен идентификатор
            </p>
            <FormGroup
              controlId="formDisallowApprovingOwnLeaves"
              label="Забрани на модератори да одобрявят/подписват собствени отсъствия"
              checkType="switch"
              name="disallow_approving_own_leaves"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Не позволява на модераторите да одобряват и подписват своите отсъствия сами
            </p>
            <FormGroup
              controlId="formGroupLeaveReview"
              label="Проверка на болнични"
              checkType="switch"
              name="leave_review"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Позволява отбелязване на болнични документи като проверени в системата
            </p>
            <FormGroup
              controlId="formGroupDisableSickLeaves"
              label="Спри качване на болнични"
              checkType="switch"
              name="disable_sick_leaves"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Скрива опцията за качване на болничен от dropdown опциите на служител
            </p>
            <FormGroup
              controlId="formGroupLeaveSubstituteEmployee"
              label="Заместващ служител при отпуск"
              checkType="switch"
              name="leave_substitute_employee"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Позволява избор на служител, който ще замести отсъстващия при създаване на отпуск
            </p>
            {isSubstituteEmployee && (
              <>
                <FormGroup
                  controlId="formGroupRequireLeaveSubstituteEmployee"
                  label="Задължителен заместващ служител при отпуск"
                  checkType="switch"
                  name="require_leave_substitute_employee"
                />
                <p className="custom-switch__desc text-muted small mb-2">
                  Изисква задължителен избор на служител, който да замести отсъстващия при създаване на отпуск
                </p>
              </>
            )}
            <FormGroup
              controlId="formGroupLeavesHalfDay"
              label="Опция за половин ден"
              checkType="switch"
              name="leaves_half_day"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Позволява заявяване на половин ден отпуск с избор на конкретна част от деня
            </p>
            {isLeavesHalfDay && (
              <>
                <FormGroup
                  controlId="formGroupShowOnlyPairedHalfDayLeaves"
                  label="Показвай само запълнени половинки дни отпуск"
                  checkType="switch"
                  name="show_only_paired_half_day_leaves"
                />
                <p className="custom-switch__desc text-muted small mb-2">
                  Показва половиния ден, само ако той е втората половина от пуснат преди това отпуск
                </p>
              </>
            )}
            <FormGroup
              controlId="formGroupViewColleagues"
              label="Видимост на отпуски на колеги"
              checkType="switch"
              name="show_colleagues_leaves"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Позволява преглед на отпуските на други членове от същата група
            </p>
            {showColleagueLeaves && (
              <>
                <FormGroup
                  controlId="formGroupColleaguesCommonGroupUsage"
                  label="Видими отпуски за Обща групa"
                  checkType="switch"
                  name="colleagues_common_group"
                />
                <p className="custom-switch__desc text-muted small mb-2">
                  Позволява преглед на отпуските на членове от общата група
                </p>
              </>
            )}
            <FormGroup
              controlId="formGroupShowWFHLeaveTypeInMainDropdown"
              label="Работа от вкъщи в основното падащо меню"
              checkType="switch"
              name="show_wfh_leave_type_in_main_dropdown"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Ако Работа от вкъщи и/или Работа от офис са активни, да излизат в основния dropdown за създаване на
              отсъствие
            </p>
            <FormGroup
              controlId="formGroupConfirmNotification"
              label="За работа от вкъщи изпращай имейл след одобрение, не след подпис"
              checkType="switch"
              name="send_leave_confirm_notification_on_approval"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Изпраща уведомление по имейл за одобрена заявка за работа от вкъщи, след одобрение, а не след подписване
              на документа
            </p>
            <FormGroup
              controlId="formGroupLimitWFHLeaveDays"
              label="Ограничен брой дни работа от вкъщи"
              checkType="switch"
              name="limit_wfh_leave_days"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Позволява задаване на максимален брой дни за работа от вкъщи на седмица или на месец
            </p>
            <FormGroup
              controlId="formGroupDisallowLeavesEOM"
              label="Забрана за отпуски в края на месеца"
              checkType="switch"
              name="disallow_leaves_near_end_of_month"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Не позволява заявяването на нов отпуск през последните X дни от месеца
            </p>
            {isDisallowLeavesEOM && (
              <FormGroup
                controlId="formGroupDisallowLeavesDaysBeforeEOM"
                label="Забрани отпуски N дни преди края на месеца"
                type="number"
                name="disallow_leaves_n_days_before_end_of_month"
                isWholeNumber
                isRequired
              />
            )}
          </Col>
          <Col md={5}>
            <FormGroup
              controlId="formGroupSickLeaveWithoutAttachment"
              label="Незадължително качване на документи за болнични"
              checkType="switch"
              name="sick_leave_without_attachment"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Позволява създаване на болничен без прикачване на допълнителен външен документ
            </p>
            <FormGroup
              controlId="formGroupArt157LeaveWithoutAttachment"
              label="Незадължително качване на документи за отпуск по чл. 157"
              checkType="switch"
              name="art157_leave_without_attachment"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Позволява създаване на отпуск по чл. 157 без прикачване на допълнителен външен документ
            </p>
            <FormGroup
              controlId="formGroupLeaveDaysAdditionalLeave"
              label="Допълнителни дни отпуск"
              checkType="switch"
              name="paid_additional_leave"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Позволява задаване на допълнителни дни отпуск, които са видими и използваеми от служителя. Смята се
              автоматично от системата.
            </p>
            {isPaidAdditionalLeaveActive && (
              <>
                <FormGroup
                  controlId="formGroupAllowAdditionalLeavesWithoutExhaustingPaidLeaves"
                  label="Допълнителен отпуск без ограничение"
                  checkType="switch"
                  name="allow_additional_leaves_without_exhausting_paid_leaves"
                />
                <p className="custom-switch__desc text-muted small mb-2">
                  Излиза поле за следене на допълнителен отпуск, но може да се ползва винаги, а не само след като е
                  използван другия платен.
                </p>
              </>
            )}
            <FormGroup
              controlId="formGroupLeaveDaysServedTime"
              label="Натрупване на дни отпуск при прослужено време"
              checkType="switch"
              name="leave_days_served_time"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Позволява проследяване на натрупаните дни отпуск в зависимост от прослуженото време
            </p>
            <FormGroup
              controlId="formGroupLeaveDaysIrregularWorkingHours"
              label="Отпуск за ненормирано работно време"
              checkType="switch"
              name="leave_days_irregular_working_hours"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Позволява задаване на допълнителен отпуск за служители с ненормирано работно време, който е видим и
              използваем от тях
            </p>
            <FormGroup
              controlId="formGroupLeaveDaysSpecificWorkingConditions"
              label="Отпуск за специфични условия на труд"
              checkType="switch"
              name="leave_days_specific_working_conditions"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Позволява задаване на допълнителен отпуск за служители, работещи при специфични условия на труд, който е
              видим и използваем от тях
            </p>
            <FormGroup
              controlId="formGroupLimitLeavesToNextMonth"
              label="Ограничаване на отпуски до следващия месец"
              checkType="switch"
              name="limit_leaves_to_next_month"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Позволява ограничаване на заявяването на отпуски само до текущия и следващ месец
            </p>
            <FormGroup
              controlId="formGroupSkipNotifyingLeaveOrderApproversForSignAction"
              label="Ограничаване на уведомления за последващи действия"
              checkType="switch"
              name="skip_notifying_leave_order_approvers_for_sign_action"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Ограничава уведомления само до конкретните действия, и не праща за последващите. Важи само ако е пуснато и
              &quot;Прилагай ограничения за уведомления по група/роля/категория&quot;
            </p>
            <FormGroup
              controlId="formGroupHourlyAbsences"
              label="Почасови отсъствия"
              checkType="switch"
              name="hourly_absences"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              Визуализира модул за създаване, заявяване и управление на почасови отсъствия
            </p>
            <FormGroup
              controlId="formGroupAlternativeStilCodes"
              label="Алтернативни кодове справка СТИЛ"
              checkType="switch"
              name="alternative_stil_codes"
            />
            <p className="custom-switch__desc text-muted small mb-2">
              В системата има един вид идентификатори за тази справка, с това се активират алтернативни, които за
              момента са забити
            </p>
          </Col>
        </Row>
      )}
    </Form>
  );
};

export default ClientForm;
